// Pure, locale-aware decimal formatters (contract 04 §3.2, §5). Rebuilt from the legacy
// `formatWithLocaleSeparator` / `formatFixed` / `formatCompact` (`format.js:322–363`), which were
// defective as a contract: they truncated instead of rounding, passed a bogus
// `formatWithLocaleSeparator:','` option, mixed browser vs workspace locale, and hardcoded
// `'en-US'`. Here every function takes an explicit `locale` and rounds by default.

import {
  SENTINEL,
  commonNumberOptions,
  getNumberFormat,
  guard,
  resolveFraction,
} from './createFormatter'
import type { FormatNumberOptions } from './types'

/** Legacy heavy-number threshold (`format.js:340`): `|v| >= 1000 ⇒ 0` fraction digits, else `2`. */
const HEAVY_THRESHOLD = 1000

/** Fixed fraction digits applied by the heavy-number default for a given magnitude. */
function heavyDigits(value: number): number {
  return Math.abs(value) >= HEAVY_THRESHOLD ? 0 : 2
}

/**
 * Locale-aware number. Rounds by default (`round: 'trunc'` opts into legacy truncation). With no
 * fraction options and `heavyDefault`, applies the `|v| >= 1000 ⇒ 0, else 2` rule; otherwise Intl's
 * natural precision. `formatNumber(1234.5, 'en-US')` → `"1,234.5"`; `(…, 'de-DE')` → `"1.234,5"`.
 */
export function formatNumber(
  value: number | null | undefined,
  locale: string,
  options: FormatNumberOptions = {},
): string {
  if (guard(value)) return SENTINEL

  const fallback =
    options.heavyDefault && options.minFrac === undefined && options.maxFrac === undefined
      ? { min: 0, max: heavyDigits(value) }
      : undefined

  return getNumberFormat(locale, {
    ...commonNumberOptions(options),
    ...resolveFraction(options, fallback),
  }).format(value)
}

/** Whole-number rendering (0 fraction digits, grouped, rounded). */
export function formatInteger(value: number | null | undefined, locale: string): string {
  if (guard(value)) return SENTINEL
  return getNumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 }).format(value)
}

/**
 * Compact notation (`1_000_000 → "1M"`), honouring the passed locale — the legacy `formatCompact`
 * hardcoded `'en-US'` (`format.js:347`). Defaults to 1 fraction digit.
 */
export function formatCompact(
  value: number | null | undefined,
  locale: string,
  options: { maxFrac?: number } = {},
): string {
  if (guard(value)) return SENTINEL
  return getNumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
    ...resolveFraction({ maxFrac: options.maxFrac ?? 1 }),
  }).format(value)
}
